import {
    ActivityProduct,
    AddonProduct,
    Agency,
    Bonus,
    Cart,
    FarePassengerType,
    FerryLeg,
    FerryProduct,
    FerrySegment,
    FlightLeg,
    FlightProduct,
    FlightSegment,
    GiftCard,
    HotelProduct,
    Payment,
    PaymentMethod,
    ProductType,
    Seating,
    SeatingProduct,
} from '@/api/Cart/cart.interfaces';
import { Baggage } from '@shared/interfaces/baggage';
import { AnyProduct } from '@shared/interfaces/product';
import { Money } from '@libs/money/money.interfaces';
import { BookingType, Item } from '../../types';
import { FerryCustomFields } from '@/tracking/analytics/Checkout/types';
import { createId } from '@/tracking/analytics/utils';
import { getDefaultDiscountPaymentMethod } from '@/tracking/analytics/Checkout/Helpers/discount';
import { getAllSeatsForFlightProduct } from '@/helpers/seating';
import {
    FerryPreAddon,
    FerryPreAddonType,
    FlightBrandedFareOption,
    Option,
} from '@shared/interfaces/preCheckout';
import { ItineraryBookData } from '@/api/Itinerary/itinerary.interfaces';
import { getSelectedBaggages } from '@shared/helpers/baggage';
import { isEmpty } from 'lodash';
import store from '@/store';
import {
    convertToDate,
    getDurationInDays,
    getMonthAsNumber,
} from '@shared/helpers/datetime/datetime';

export const mapCartAsItems = (cart: Cart): Item[] => {
    const products: Item[] = mapProductsAsItems([...cart.Products]);
    const discountItems = mapDiscountsAsItems(cart.Payment);
    const agencyCommissions = mapAgencyCommissionAsItem(cart?.Agency);
    const amountAlreadyPaid = mapAmountAlreadyPaidAsItem(cart.Payment?.AmountAlreadyPaid);
    const bonusNightItems = cart.AvailableBonus.filter(({ IsApplied }) => IsApplied).map(
        (bonus) => mapBonusNightAsItem(bonus)
    );

    return [
        ...products,
        ...discountItems,
        ...bonusNightItems,
        ...agencyCommissions,
        ...amountAlreadyPaid,
    ];
};

export const mapProductsAsItems = (products: AnyProduct[]): Item[] => {
    const items: Item[] = [];
    [...products].forEach((p) => {
        switch (p.Type) {
            case ProductType.Flight: {
                const flight = p as FlightProduct;
                const seats = getAllSeatsForFlightProduct(flight);
                items.push(mapFlightProductItem(flight));
                if (flight?.Seating?.Price?.Amount) {
                    items.push(mapSeatingItem(flight.Seating, flight));
                }
                flight.Pax.forEach((pax) => {
                    const selected = getSelectedBaggages(pax.AdditionalBaggage);

                    if (!isEmpty(selected)) {
                        selected.forEach((bag) => items.push(mapBaggageItem(bag, flight)));
                    }
                });
                seats.forEach((seat) => items.push(mapGordianSeatingItem(seat, flight)));
                break;
            }
            case ProductType.Ferry:
                items.push(mapFerryProductItem(p as FerryProduct));
                break;
            case ProductType.Hotel:
                items.push(mapHotelProductItem(p as HotelProduct));
                break;
            case ProductType.Golf:
                items.push(mapGolfProductItem(p as HotelProduct));
                break;
            case ProductType.Activity:
                items.push(mapActivityProductItem(p as ActivityProduct));
                break;
            case ProductType.Addon:
                items.push(mapAddonProductAsItem(p as AddonProduct));
                break;
            default:
                items.push(mapAnyProductItem(p));
                break;
        }
    });

    return items;
};

const mapDiscountsAsItems = (payment: Payment): Item[] => {
    const isPaymentDiscountApplied = store.getters[
        'cart/paymentMethodDiscountApplies'
    ] as boolean;
    const items: Item[] = [];
    if (payment.CampaignDiscount) {
        items.push(mapCampaignDiscountItem(payment.CampaignDiscount));
    }
    if (isPaymentDiscountApplied) {
        const defaultDiscountPaymentMethod = getDefaultDiscountPaymentMethod(payment);
        if (defaultDiscountPaymentMethod && defaultDiscountPaymentMethod.Discount) {
            items.push(
                mapPaymentDiscountItem(
                    defaultDiscountPaymentMethod.Discount,
                    defaultDiscountPaymentMethod.Name
                )
            );
        }
    }
    payment.GiftCards.forEach((giftCard) => {
        items.push(mapGiftCardAsItem(giftCard));
    });
    return items;
};

const mapFlightLeg = (leg: FlightLeg) => {
    const { DepartureAirportCode, DepartureDate } = leg.Segments[0] as FlightSegment;
    const { ArrivalDate, ArrivalAirportCode } = leg.Segments[
        leg.Segments.length - 1
    ] as FlightSegment;
    return {
        origin: DepartureAirportCode,
        startDate: DepartureDate,
        endDate: ArrivalDate,
        destination: ArrivalAirportCode,
    };
};

const getLengthOfStay = (outbound: FlightLeg, inbound: FlightLeg) => {
    if (inbound === undefined) {
        return null;
    }

    const mappedOutbound = mapFlightLeg(outbound);
    const mappedInbound = mapFlightLeg(inbound);
    return getDurationInDays(
        convertToDate(mappedOutbound.startDate),
        convertToDate(mappedInbound.startDate)
    );
};

const getDepartureMonth = (outbound: FlightLeg) => {
    const mappedOutbound = mapFlightLeg(outbound);
    return getMonthAsNumber(convertToDate(mappedOutbound.startDate));
};

const mapFlightProductItem = (product: FlightProduct, quantity = 1): Item => {
    const fareTypes = product.PriceBreakdown?.FareTypes;
    const [outbound, inbound] = product.Legs;
    const getNumberOfPaxByType = (fareType: FarePassengerType) => {
        if (fareTypes) {
            return fareTypes.find(({ FareType }) => FareType === fareType)?.NumberOfPax ?? 0;
        }
        return 0;
    };

    return {
        quantity,
        item_id: product.Id,
        item_name: product.Description,
        item_category: 'Transport',
        item_category2: product?.Type,
        price: product.Price.Amount,
        flight: {
            adults: getNumberOfPaxByType(FarePassengerType.Adult),
            children: getNumberOfPaxByType(FarePassengerType.Child),
            youths: getNumberOfPaxByType(FarePassengerType.Youth),
            infants: getNumberOfPaxByType(FarePassengerType.Infant),
            outbound: mapFlightLeg(outbound),
            ...(inbound && { inbound: mapFlightLeg(inbound) }),
        },
    };
};

// TODO: ask for more seating info from BE
const mapSeatingItem = (seat: Seating, flight: FlightProduct, quantity = 1): Item => ({
    quantity,
    item_id: seat.SeatMapIdentifier,
    item_parent_id: flight?.Id,
    item_name: 'Seating',
    item_category: 'Transport',
    item_category2: flight?.Type,
    item_category3: 'Seating',
    price: seat.Price.Amount,
});

export const mapBaggageItem = (
    baggage: Baggage,
    flight: FlightProduct,
    quantity = 1
): Item => ({
    quantity,
    item_id: baggage.Id,
    item_parent_id: flight.Id,
    item_name: baggage.Type,
    item_variant: baggage.Description,
    item_category: 'Transport',
    item_category2: flight?.Type,
    item_category3: 'Baggage',
    price: baggage.Price.Amount,
});

const mapFerryProductItem = (ferry: FerryProduct, quantity = 1): Item => {
    const addFerryData = ferry.Pax?.length && ferry.Legs?.length;

    const mapLeg = (leg: FerryLeg) => {
        const { DeparturePortCode, DepartureDate } = leg.Segments[0] as FerrySegment;
        const { ArrivalDate, ArrivalPortCode } = leg.Segments[
            leg.Segments.length - 1
        ] as FerrySegment;
        return {
            origin: DeparturePortCode,
            startDate: DepartureDate,
            endDate: ArrivalDate,
            destination: ArrivalPortCode,
        };
    };
    const getFerryData = (): FerryCustomFields => ({
        ...mapLeg(ferry.Legs[0]),
        adults: ferry.Pax.filter((p) => p.PaxDescription?.IsAdult).length,
        children: ferry.Pax.filter((p) => !p.PaxDescription?.IsAdult).length,
    });

    return {
        quantity,
        item_id: ferry.Id,
        item_name: ferry.Description,
        item_category: 'Transport',
        item_category2: ferry?.Type,
        price: ferry.Price.Amount,
        ...(addFerryData && { ferry: getFerryData() }),
    };
};

const mapHotelProductItem = (hotel: HotelProduct, quantity = 1): Item => ({
    quantity,
    item_id: hotel.Id,
    item_name: hotel.Description,
    item_category: BookingType.Accommodation,
    item_category2: 'Hotel',
    price: hotel.Price.Amount,
    hotel: {
        destination: hotel.Destination,
        start_date: hotel.FromDate,
        end_date: hotel.ToDate,
        rooms: hotel.Rooms.map((room) => ({
            description: room.Description,
            price: room.Price.Amount,
            adults: room.PaxDescription.filter(({ IsAdult }) => IsAdult).length,
            children: room.PaxDescription.filter(({ IsAdult }) => !IsAdult).length,
        })),
    },
});

const mapAnyProductItem = (product: AnyProduct, quantity = 1): Item => {
    return {
        quantity,
        item_id: product.Id,
        item_name: product.Description,
        item_category: product.Type,
        price: product.Price.Amount,
    };
};

const mapGolfProductItem = (golf: HotelProduct, quantity = 1): Item => ({
    quantity,
    item_id: golf.Id,
    item_name: golf.Description,
    item_category: 'Golf',
    item_category2: 'Ticket',
    price: golf.Price.Amount,
    golf: {
        fromDate: golf.FromDate,
        toDate: golf.ToDate,
        destination: golf.Destination,
        code: golf.HotelCode,
        tickets: golf.Rooms?.length,
    },
});

const mapActivityProductItem = (activity: ActivityProduct, quantity = 1): Item => ({
    quantity,
    item_id: activity.Id,
    item_name: activity.Description,
    item_category: 'Activity',
    item_category2: activity?.Type,
    price: activity.Price.Amount,
    activity: {
        date: activity.Date,
        placeCode: activity.PlaceCode,
        tickets: activity.Tickets.map((ticket) => ({
            description: ticket.Description,
            price: ticket.Price.Amount,
        })),
    },
});

export const mapCampaignDiscountItem = (discount: Money, quantity = 1): Item => ({
    quantity,
    item_id: createId(),
    item_name: 'CampaignDiscount',
    item_category: 'Discount',
    item_category2: 'Campaign',
    discount: discount.Amount,
    price: -discount.Amount,
});

const mapPaymentDiscountItem = (
    discount: Money,
    paymentMethodName: string,
    quantity = 1
): Item => ({
    quantity,
    item_id: paymentMethodName,
    item_name: paymentMethodName,
    item_category: 'Discount',
    item_category2: 'Payment',
    discount: discount.Amount,
    price: -discount.Amount,
});

const mapAgencyCommissionAsItem = (
    agency: Agency | null | undefined,
    quantity = 1
): Item[] => {
    if (agency?.Commission?.Amount) {
        return [
            {
                quantity,
                item_id: `Agency_${agency?.AgencyNumber}`,
                item_name: 'AgencyCommission',
                item_category: 'Commission',
                item_category2: 'Agency',
                discount: agency.Commission.Amount,
                price: -agency.Commission.Amount,
            },
        ];
    }
    return [];
};

export const mapGiftCardAsItem = (giftCard: GiftCard, quantity = 1): Item => ({
    quantity,
    item_id: giftCard.PaymentId ?? 'giftCardId',
    item_name: 'PaymentGiftCard',
    item_category: 'Discount',
    item_category2: 'GiftCard',
    discount: giftCard.Value.Amount,
    price: -giftCard.Value.Amount,
});

const mapBonusNightAsItem = (bonus: Bonus, quantity = 1): Item => ({
    quantity,
    item_id: bonus.Id,
    item_name: 'BonusNight',
    item_category: 'Discount',
    item_category2: 'Bonus',
    discount: bonus.Value.Amount,
    price: -bonus.Value.Amount,
});

export const mapPaymentMethodAsItem = (paymentMethod: PaymentMethod, quantity = 1): Item => {
    const discount = paymentMethod.Discount?.Amount;
    return {
        quantity,
        ...(discount && {
            discount,
        }),
        item_id: paymentMethod?.Code,
        item_name: paymentMethod?.Name,
        item_category: 'Payment',
        item_category2: 'Method',
        price: discount ? -discount : 0,
    };
};

const mapAddonProductAsItem = (addonProduct: AddonProduct, quantity = 1): Item => {
    return {
        quantity,
        item_id: addonProduct.Id,
        item_name: addonProduct.Description,
        item_category: 'Addon',
        item_category2: addonProduct?.$type,
        price: addonProduct.Price.Amount,
    };
};

const mapAmountAlreadyPaidAsItem = (
    amount: Money | null | undefined,
    quantity = 1
): Item[] => {
    if (amount?.Amount) {
        return [
            {
                quantity,
                item_id: `AmountAlreadyPaid`,
                item_name: 'AmountAlreadyPaid',
                item_category: 'Payment',
                item_category2: 'AmountAlreadyPaid',
                price: -amount.Amount,
            },
        ];
    }
    return [];
};

export const mapGordianSeatingItem = (
    seat: SeatingProduct,
    flight: FlightProduct,
    quantity = 1
): Item => ({
    quantity,
    item_id: seat.Id,
    item_parent_id: flight.Id,
    item_name: 'GordianSeating',
    item_variant: seat.Description ?? `Row: ${seat.Row}, Column: ${seat.Column}`,
    item_category: 'Transport',
    item_category2: flight?.Type,
    item_category3: 'Seating',
    price: seat.Price.Amount,
});

export const mapPreCheckoutOptionAsItem = (
    option: Option,
    addon: FerryPreAddon,
    quantity = 1
): Item => ({
    quantity,
    item_id: option.Id,
    item_name: option.Title || '',
    item_category: 'PreCheckout',
    item_category2: 'Addon',
    item_category3: FerryPreAddonType[addon.Type],
    price: option.Price.Amount,
});

export const mapPreCheckoutBrandedOptionAsItem = (
    option: FlightBrandedFareOption,
    quantity = 1
): Item => ({
    quantity,
    item_id: option.Itinerary.BookItineraryId,
    item_name: option.BrandName || '',
    item_category: 'PreCheckout',
    item_category2: 'Addon',
    price: option.PriceDiff.Amount,
});

export const mapItineraryBookItem = (
    flight: ItineraryBookData,
    isRebookable: boolean,
    quantity = 1
): Item => {
    const price = isRebookable
        ? flight?.RebookableBookPrice?.Amount
        : flight?.BookPrice?.Amount;

    const itineraryId = isRebookable ? flight.RebookableItineraryId : flight.BookItineraryId;

    const [outbound, inbound] = flight.Legs;
    const [departureSegment] = outbound.Segments;
    const arrivalSegment = outbound.Segments[outbound.Segments.length - 1];

    return {
        quantity,
        price,
        item_id: itineraryId ?? '',
        item_name: `Flyg: ${departureSegment.DepartureAirportName} - ${arrivalSegment.ArrivalAirportName}`,
        item_category: 'Transport',
        item_category2: 'Flight',
        item_category3: isRebookable ? 'Rebookable ticket' : 'Regular ticket',
        flight: {
            adults: flight.Adults ?? 0,
            children: flight.Children ?? 0,
            youths: 0,
            infants: flight.Infants ?? 0,
            lengthOfStay: getLengthOfStay(outbound, inbound),
            departureMonth: getDepartureMonth(outbound),
            outbound: mapFlightLeg(outbound),
            ...(inbound && { inbound: mapFlightLeg(inbound) }),
        },
    };
};
